import React, { useState, useRef, useCallback, useEffect } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import { Typography } from "@mui/material";
import GameHUD from "./components/GameHUD";
import GameBoard from "./components/GameBoard";
import HowToPlayModal from "./components/HowToPlayModal";
import EndGameModal from "./components/EndGameModal";
import { Howl } from "howler";
import seedrandom from "seedrandom";
import CalendarArchive from "../components/CalendarArchive";
import { formatDateForStorage } from "../../../components/GameStateUtil";
import {
  getPonderStreakFromStorage,
  updatePonderStreak,
  getIsPonderStreakActive,
} from "../components/PonderStreakUtil";

const NUM_COLS = 4;
const NUM_ROWS = 6; // Visible rows
const STARTING_BLOCKS = [2, 4];

// Helper to get today's date as seed string (YYYY-MM-DD)
function getTodaySeed() {
  const today = new Date();
  return today.toISOString().slice(0, 10);
}

// Helper to get seed string for a given date (YYYY-MM-DD)
function getDateSeed(date) {
  return date.toISOString().slice(0, 10);
}

// Weighted random block generator using a seeded RNG
function getRandomBlock(rng = Math.random) {
  const choices = [
    { value: 2, weight: 50 },
    { value: 4, weight: 25 },
    { value: 8, weight: 15 },
    { value: 16, weight: 7 },
    { value: 32, weight: 2 },
    { value: 64, weight: 1 },
  ];
  const totalWeight = choices.reduce((sum, c) => sum + c.weight, 0);
  let rand = rng() * totalWeight;
  for (const choice of choices) {
    if (rand < choice.weight) return choice.value;
    rand -= choice.weight;
  }
  return 2;
}

// Deterministic block generator for a given date and step
function getRandomBlockForStep(dateSeed, step) {
  const rng = seedrandom(`${dateSeed}-${step}`);
  return getRandomBlock(rng);
}

const createEmptyBoard = () =>
  Array.from({ length: NUM_ROWS }, () => Array(NUM_COLS).fill(null));

const DoubleUp = () => {
  const [board, setBoard] = useState(createEmptyBoard());
  const [currentDate, setCurrentDate] = useState(new Date());
  const seed = getDateSeed(currentDate);
  const [step, setStep] = useState(0);
  const [nextBlock, setNextBlock] = useState(() =>
    getRandomBlockForStep(seed, 0)
  );
  const [score, setScore] = useState(0); // Highest block
  const [bestScore, setBestScore] = useState(() => {
    const saved = localStorage.getItem("doubleUpBestScore");
    return saved ? parseInt(saved) : 0;
  });
  const [streak, setStreak] = useState(0);
  const [isStreakActive, setIsStreakActive] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showHowToModal, setShowHowToModal] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [mergingTile, setMergingTile] = useState(null);
  const [currentMergeSound, setCurrentMergeSound] = useState(null);
  const [mergeCount, setMergeCount] = useState(0);
  const [isGameOver, setIsGameOver] = useState(false);
  const [showEndGameModal, setShowEndGameModal] = useState(false);
  const [newBestScore, setNewBestScore] = useState(false);

  // Add sound instances
  const clickSound = useRef(
    new Howl({
      src: ["/assets/audio/click.ogg", "/assets/audio/click.mp3"],
      volume: 0.5,
    })
  );
  const errorSound = useRef(
    new Howl({
      src: ["/assets/audio/error.ogg", "/assets/audio/error.mp3"],
      volume: 0.2,
    })
  );
  const mergeSound = useRef(
    new Howl({
      src: ["/assets/audio/merge2.mp3"],
      volume: 0.3,
    })
  );
  const comboMergeSound = useRef(
    new Howl({
      src: ["/assets/audio/merge2.mp3"],
      volume: 0.4,
      rate: 1.1, // Slightly faster pitch
    })
  );
  const superComboMergeSound = useRef(
    new Howl({
      src: ["/assets/audio/merge2.mp3"],
      volume: 0.5,
      rate: 1.2, // Even faster pitch
    })
  );

  const playSound = (sound) => {
    if (!isMuted) {
      sound.current.play();
    }
  };

  // Find the first empty row from the bottom in a column
  const getDropRow = (col) => {
    for (let row = NUM_ROWS - 1; row >= 0; row--) {
      if (board[row][col] === null) return row;
    }
    return null; // Column full
  };

  const handleDrop = (col) => {
    const row = getDropRow(col);
    if (row === null) {
      playSound(errorSound);
      return; // Column full
    }

    const newBoard = board.map((r) => [...r]);
    // Place the block
    newBoard[row][col] = nextBlock;

    // Check if this will be a merge
    const willMerge =
      row < NUM_ROWS - 1 && newBoard[row + 1][col] === nextBlock;

    // Only play click sound if it's not going to merge
    if (!willMerge) {
      playSound(clickSound);
    }

    // Reset merge count for new drop
    setMergeCount(0);

    // Perform merges with staggered sounds
    let mergeRow = row;
    let currentCount = 0;

    // Start first merge if needed
    if (willMerge) {
      currentCount++;
      setMergeCount(currentCount);
      const newMergeSound = new Howl({
        src: ["/assets/audio/merge2.mp3"],
        volume: 0.3 + currentCount * 0.1,
        rate: 0.5 + currentCount * 0.1,
      });
      setCurrentMergeSound(newMergeSound);

      newBoard[row + 1][col] *= 2;
      newBoard[row][col] = null;
      setMergingTile({ row: row + 1, col });
      setScore((prev) => Math.max(prev, newBoard[row + 1][col]));
    }

    // Update score for initial placement
    setScore((prev) => Math.max(prev, nextBlock));
    setBoard(newBoard);
    // Advance step and set next block ONLY when a new tile is dropped
    setStep((prevStep) => {
      const newStep = prevStep + 1;
      setNextBlock(getRandomBlockForStep(seed, newStep));
      return newStep;
    });
  };

  // Create handlers that will be passed to GameBoard
  const handleMergeAnimationStart = useCallback(() => {
    if (currentMergeSound) {
      currentMergeSound.play();
    }
  }, [currentMergeSound]);

  const handleMergeAnimationEnd = useCallback(() => {
    if (mergingTile) {
      const { row, col } = mergingTile;
      const nextRow = row + 1;

      if (nextRow < NUM_ROWS && board[nextRow][col] === board[row][col]) {
        const newCount = mergeCount + 1;
        setMergeCount(newCount);

        // Create next merge sound
        const newMergeSound = new Howl({
          src: ["/assets/audio/merge2.mp3"],
          volume: 0.3 + newCount * 0.1,
          rate: 0.5 + newCount * 0.1,
        });
        setCurrentMergeSound(newMergeSound);

        // Update the board for next merge
        const newBoard = board.map((r) => [...r]);
        newBoard[nextRow][col] *= 2;
        newBoard[row][col] = null;

        // Set merging tile for animation
        setMergingTile({ row: nextRow, col });
        // Update score if new block is higher
        setScore((prev) => Math.max(prev, newBoard[nextRow][col]));
        setBoard(newBoard);
      } else {
        // All merges complete
        // Do NOT advance step or set next block here!
        setMergingTile(null);
        setCurrentMergeSound(null);
        setMergeCount(0);
      }
    }
  }, [board, mergingTile, mergeCount, seed]);

  // Check if the game is over (all columns are full)
  const checkGameOver = useCallback(() => {
    for (let col = 0; col < NUM_COLS; col++) {
      if (board[0][col] === null) {
        return false; // Found an empty space, game is not over
      }
    }
    return true; // All columns are full
  }, [board]);

  // Update best score when score changes
  useEffect(() => {
    if (score > bestScore) {
      setBestScore(score);
      setNewBestScore(true);
      localStorage.setItem("doubleUpBestScore", score.toString());

      // Store game data with date
      const dateKey = formatDateForStorage(currentDate);
      const gameData = {
        completed: isGameOver,
        score: score,
        best: score,
      };
      localStorage.setItem(`doubleup-${dateKey}`, JSON.stringify(gameData));
    }
  }, [score, bestScore, currentDate, isGameOver]);

  // Check for game over after each move
  useEffect(() => {
    if (checkGameOver()) {
      setIsGameOver(true);
      setShowEndGameModal(true);

      // Store game completion data
      const dateKey = formatDateForStorage(currentDate);
      const gameData = {
        completed: true,
        score: score,
        best: bestScore,
      };
      localStorage.setItem(`doubleup-${dateKey}`, JSON.stringify(gameData));

      // Update streak
      const newStreak = updatePonderStreak(new Date(), "doubleup");
      setStreak(newStreak);
      setIsStreakActive(true);
    }
  }, [board, checkGameOver, currentDate, score, bestScore]);

  // Add effect for keyboard shortcut
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Check for Ctrl/Cmd + Shift + A
      if (
        (e.ctrlKey || e.metaKey) &&
        e.shiftKey &&
        e.key.toLowerCase() === "a"
      ) {
        e.preventDefault();
        if (!isGameOver) {
          // Fill the board to trigger game over
          const filledBoard = board.map((row) => row.map(() => 2));
          setBoard(filledBoard);
          setIsGameOver(true);
          setShowEndGameModal(true);
        }
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [board, isGameOver]);

  // Add effect to load streak when date changes
  useEffect(() => {
    const { count } = getPonderStreakFromStorage("doubleup");
    setStreak(count);
    setIsStreakActive(getIsPonderStreakActive("doubleup"));
  }, [currentDate]);

  // Add effect to handle date changes
  useEffect(() => {
    setBoard(createEmptyBoard());
    setStep(0);
    setNextBlock(getRandomBlockForStep(seed, 0));
    setScore(0);
    setIsGameOver(false);
    setShowEndGameModal(false);
    setNewBestScore(false);
    setMergingTile(null);
    setCurrentMergeSound(null);
    setMergeCount(0);
  }, [currentDate, seed]);

  const handlePlayAgain = () => {
    setBoard(createEmptyBoard());
    setStep(0);
    setNextBlock(getRandomBlockForStep(seed, 0));
    setScore(0);
    setIsGameOver(false);
    setShowEndGameModal(false);
    setNewBestScore(false);
    setMergingTile(null);
    setCurrentMergeSound(null);
    setMergeCount(0);
  };

  const handleDateSelect = (date) => {
    setCurrentDate(date);
    setShowCalendar(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        bgcolor: "#F5F0EB",
        p: 1,
        height: "100vh",
      }}
    >
      <Paper
        elevation={2}
        sx={{
          width: "100%",
          maxWidth: "sm",
          p: 2,
          mb: 2,
          borderRadius: 2,
          bgcolor: "white",
        }}
      >
        <GameHUD
          score={score}
          bestScore={bestScore}
          nextBlock={nextBlock}
          currentDate={currentDate}
          streak={streak}
          isStreakActive={isStreakActive}
          isMuted={isMuted}
          onHelp={() => setShowHowToModal(true)}
          onMute={() => setIsMuted((m) => !m)}
          onDateClick={() => setShowCalendar(true)}
        />
      </Paper>
      <GameBoard
        board={board}
        onDrop={handleDrop}
        numCols={NUM_COLS}
        mergingTile={mergingTile}
        onMergeAnimationStart={handleMergeAnimationStart}
        onMergeAnimationEnd={handleMergeAnimationEnd}
      />
      <HowToPlayModal
        open={showHowToModal}
        onClose={() => setShowHowToModal(false)}
      />
      <EndGameModal
        open={showEndGameModal}
        onClose={() => setShowEndGameModal(false)}
        onPlayAgain={handlePlayAgain}
        score={score}
        bestScore={bestScore}
        newBestScore={newBestScore}
      />
      <CalendarArchive
        open={showCalendar}
        onClose={() => setShowCalendar(false)}
        gameKey="doubleup"
        getGameDataForDate={(dateKey) => {
          const data = localStorage.getItem(`doubleup-${dateKey}`);
          if (!data) {
            return null;
          }
          const gameData = JSON.parse(data);
          return {
            completed: gameData.completed,
            score: gameData.score,
            best: gameData.best ? `${gameData.best} points` : null,
          };
        }}
        formatDateForStorage={formatDateForStorage}
        title="DoubleUp Archive"
        description="View your past DoubleUp games and achievements"
        onDateSelect={handleDateSelect}
        currentDate={currentDate}
      />
    </Box>
  );
};

export default DoubleUp;
